import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const fmtDate = (d) => d
  ? new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const OsBadge = ({ os }) => {
  const cfg = {
    Android: ['#dcfce7', '#166534', '🤖 Android'],
    iOS:     ['#dbeafe', '#1e40af', '🍎 iOS'],
  };
  const [bg, color, label] = cfg[os] || ['#f1f5f9', '#64748b', os || '—'];
  return <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{label}</span>;
};

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get('/admin/devices')
      .then(({ data }) => setDevices(Array.isArray(data) ? data : []))
      .catch(e => setErr(e.response?.data?.message || 'Impossible de charger les appareils'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return devices;
    const s = q.toLowerCase();
    return devices.filter(d =>
      (d.user_name || '').toLowerCase().includes(s) ||
      (d.user_phone || '').includes(s) ||
      (d.device_name || '').toLowerCase().includes(s) ||
      (d.brand || '').toLowerCase().includes(s) ||
      (d.model || '').toLowerCase().includes(s) ||
      (d.android_id || '').toLowerCase().includes(s) ||
      (d.ios_idfv || '').toLowerCase().includes(s) ||
      (d.last_ip || '').includes(s)
    );
  }, [devices, q]);

  const exportCsv = () => {
    const header = ['User', 'Phone', 'Device', 'Brand', 'Model', 'OS', 'OS Version', 'Build ID', 'Android ID', 'iOS IDFV', 'App version', 'Last IP', 'User-Agent', 'First seen', 'Last seen', 'Login count'];
    const rows = filtered.map(d => [
      d.user_name, d.user_phone, d.device_name, d.brand, d.model, d.os_name, d.os_version,
      d.os_build_id, d.android_id, d.ios_idfv, d.app_version, d.last_ip,
      (d.user_agent || '').replace(/[,\n\r]/g, ' '),
      d.first_seen, d.last_seen, d.login_count,
    ].map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `devices-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
      <div className="spinner" />
    </div>
  );

  if (err) return (
    <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--danger)' }}>⚠️ {err}</div>
  );

  return (
    <div className="animate-fade">
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Appareils utilisateurs</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Empreintes appareils collectées à chaque connexion — destinées aux audits et réquisitions judiciaires
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Rechercher (nom, IP, ID, modèle…)"
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', minWidth: 280, fontSize: 13 }}
          />
          <button onClick={exportCsv} className="btn" style={{ background: 'var(--primary)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            ⬇ Export CSV
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            {filtered.length} appareil(s) {q ? `(filtré sur ${devices.length})` : ''}
          </div>

          {filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
              Aucun appareil enregistré
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                    <th style={th}>Utilisateur</th>
                    <th style={th}>Appareil</th>
                    <th style={th}>OS</th>
                    <th style={th}>IP</th>
                    <th style={th}>Dernière connexion</th>
                    <th style={{ ...th, textAlign: 'right' }}>Connexions</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(d => (
                    <>
                      <tr key={d.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{d.user_name || `User ${d.user_id}`}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.user_phone}</div>
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 500 }}>{d.device_name || '—'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.brand} {d.model}</div>
                        </td>
                        <td style={td}>
                          <OsBadge os={d.os_name} />
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{d.os_version}</div>
                        </td>
                        <td style={td}>{d.last_ip || '—'}</td>
                        <td style={td}>{fmtDate(d.last_seen)}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{d.login_count}</td>
                        <td style={td}>
                          <button
                            onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 12, fontWeight: 600 }}
                          >
                            {expanded === d.id ? 'Masquer' : 'Détails'}
                          </button>
                        </td>
                      </tr>
                      {expanded === d.id && (
                        <tr key={`${d.id}-detail`} style={{ background: 'var(--surface-2)' }}>
                          <td colSpan={7} style={{ padding: '16px 20px', fontSize: 12 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                              <Detail label="Device ID (UUID)" value={d.device_id} />
                              <Detail label="Android ID" value={d.android_id} />
                              <Detail label="iOS IDFV" value={d.ios_idfv} />
                              <Detail label="Build ID" value={d.os_build_id} />
                              <Detail label="Version app" value={d.app_version} />
                              <Detail label="Première connexion" value={fmtDate(d.first_seen)} />
                              <Detail label="User-Agent" value={d.user_agent} fullWidth />
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Detail = ({ label, value, fullWidth }) => (
  <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
    <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{value || '—'}</div>
  </div>
);

const th = { padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)' };
const td = { padding: '12px 8px', color: 'var(--text-primary)', verticalAlign: 'top' };
